import { useCallback, useRef, useState } from 'react';
import { ConditionType, defaultOperator, getRootOperator, isNullOperator, OperatorType, SortDir } from '@fabric-ui/components';
import { getIfValidStringifiedObject } from '@fabric-ui/utils';
import { useDebounceV1 } from '../../../hooks/useDebounce';
import { FieldFilterMode, FieldFilterModeType, isAdvancedFilterMode } from '../../../utils/filterUtils';
import { FINDING_STATE_LABELS } from '../../../utils/filterValueLabels';
import { getEscapedValue, getNestedValue, setNestedValue } from '../../../utils/utils';
import { getField } from '../../FieldFilter.utils';
import { useExpressionBuilder } from '../ExpressionBuilderContext';
import { AdvancedFilterValue, ConditionOperand, FieldFilterValue } from '../types';

export const findingStateOptions = Object.entries(FINDING_STATE_LABELS).map(([value, title]) => ({ title, value }));

const emptyOperand: Record<string, ConditionOperand> = ['string', 'number', 'IP', 'FIX'].reduce(
  (obj, key) => ({
    ...obj,
    [key]: { operator: defaultOperator[ConditionType[key]], value: '' },
    [`${key}[]`]: { operator: `${defaultOperator[ConditionType[key]]}|ANY`, value: '' },
  }),
  {}
);

interface UseFieldFilterStateParams {
  field: string;
  filters: Record<string, unknown>;
  type: string;
  onClose: () => void;
}

export const useFieldFilterState = ({ field, filters, type, onClose }: UseFieldFilterStateParams) => {
  const { defaultMode } = useExpressionBuilder();
  const isNumberField = type === 'number';
  const filterObject = getIfValidStringifiedObject(filters[field] as string) as FieldFilterValue | undefined;

  const [advancedFilterValue, setAdvancedFilterValue] = useState<AdvancedFilterValue>(() => {
    if (
      filterObject?.type !== undefined ||
      filterObject?.dir !== undefined ||
      filterObject?.top !== undefined ||
      filterObject?.value !== undefined
    ) {
      return {
        type: filterObject.type,
        dir: filterObject.dir as SortDir | undefined,
        top: filterObject.top,
        value: filterObject.value as string | undefined,
      };
    }
    return {};
  });

  const [mode, setMode] = useState<FieldFilterModeType>(() => {
    if (filterObject) {
      if (filterObject?.type !== undefined || filterObject?.top !== undefined || filterObject?.byValue !== undefined) {
        return FieldFilterMode.ADVANCED;
      }
      const values = filterObject?.and ?? filterObject?.or ?? [];
      const hasValues = Array.isArray(values) ? values.some(c => c?.value) : !!filterObject?.value;
      return hasValues ? FieldFilterMode.CONDITION : (defaultMode ?? FieldFilterMode.CONDITION);
    }
    return defaultMode ?? FieldFilterMode.LIST;
  });

  // TODO: Remove when AVA-12635 is implemented.
  // When null isn't first in the array it is parsed incorrectly
  const value = filterObject || getField(filters[field])?.map(val => (val === 'null' ? null : val)) || [];

  const initialFilter = isAdvancedFilterMode(mode) ? {} : emptyOperand[type];
  const [conditionValue, setConditionValue] = useState<FieldFilterValue>(
    filterObject && !isAdvancedFilterMode(mode) ? filterObject : { [isNumberField ? 'or' : 'and']: [initialFilter] }
  );
  const operator = getRootOperator<OperatorType>(conditionValue);
  const valueOperator = getRootOperator(filterObject as Record<string, unknown>);
  const buttonRef = useRef(null);

  const [errors, setErrors] = useState<ConditionOperand[]>([]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedValue = useDebounceV1(search, 500);
  const escapedValue = getEscapedValue(debouncedValue);
  const searchCondition = debouncedValue ? `value LIKE '%${escapedValue}%'` : undefined;

  const handleSelectConditionChange = useCallback(
    (value: unknown, index: number) => {
      setErrors([]);
      setConditionValue(prev => {
        const nestedPrev = getNestedValue(`${operator}.${index}`, prev);
        const newValue = isNullOperator(value) ? '' : nestedPrev.value;
        return setNestedValue(`${operator}.${index}`, prev, { operator: value, value: newValue });
      });
    },
    [operator]
  );

  const handleClose = useCallback(() => {
    setOpen(false);
    onClose();
  }, [onClose]);

  const handleAdvancedFilterChange = useCallback((newAdvancedFilterValue: AdvancedFilterValue) => {
    setAdvancedFilterValue(newAdvancedFilterValue);
  }, []);

  return {
    filterObject,
    advancedFilterValue,
    setAdvancedFilterValue,
    mode,
    setMode,
    value,
    initialFilter,
    conditionValue,
    setConditionValue,
    operator,
    valueOperator,
    buttonRef,
    errors,
    setErrors,
    open,
    setOpen,
    search,
    setSearch,
    debouncedValue,
    escapedValue,
    searchCondition,
    handleSelectConditionChange,
    handleClose,
    handleAdvancedFilterChange,
  };
};
